/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { Injectable } from '@angular/core';
import { clusterCoordinator, clusterTopologyLoaded } from '@app/core/ngrx/app.selectors';
import { AppState } from '@app/core/types';
import { Store } from '@ngrx/store';
import { ClusterCredentials } from '@shared/types/cluster';
import {
  ActionArgument,
  ActionName,
  ClusterAction,
  ClusterActionFailedResponse,
  ClusterActionResponse,
  ClusterActionResponseError,
  DistributedActions,
  DualActions,
  ResponseStatus,
  TaskResponse,
} from '@shared/types/cluster-actions';
import { EMPTY, Observable, combineLatest, merge, of, throwError, zip } from 'rxjs';
import { concatMap, filter, map, scan, shareReplay, switchMap, take, takeWhile, tap } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';
import { WebSocketConnection } from './websocket-connection.service';

interface DispatchArgs {
  clusterId: string;
}

interface DistributedArgs {
  clusterId: string;
  nodeIds: string[];
}

interface DispatchArgsWithArgument<T extends ActionName> extends DispatchArgs {
  argument: ActionArgument[T];
}

interface DistributedArgsWithArgument<T extends ActionName> extends DistributedArgs {
  argument: ActionArgument[T];
}

type ActionArgs<T extends ActionName> = ActionArgument[T] extends null ? ArgsWithoutArgument<T> : ArgsWithArgument<T>;

type ArgsWithoutArgument<T extends ActionName> = T extends DualActions
  ? DistributedArgs | DispatchArgs
  : T extends DistributedActions
    ? DistributedArgs
    : DispatchArgs;

type ArgsWithArgument<T extends ActionName> = T extends DualActions
  ? DispatchArgsWithArgument<T> | DistributedArgsWithArgument<T>
  : T extends DistributedActions
    ? DistributedArgsWithArgument<T>
    : DispatchArgsWithArgument<T>;

const isDistributed = <T extends DispatchArgs, S extends DistributedArgs>(args: T | S): args is S => 'nodeIds' in args;

type DispatchReturnType<T extends ActionName> =
  ActionArgs<T> extends DistributedArgs
    ? Observable<{ job: ClusterActionResponse<T>; jobCount: number }>
    : Observable<ClusterActionResponse<T>>;

type ClusterActionDispatchOptions = {
  credentials?: ClusterCredentials;
};

@Injectable({ providedIn: 'root' })
export class ClusterActionService {
  constructor(
    private ws: WebSocketConnection,
    private store: Store<AppState>,
  ) {}

  private receipts = new Set<string>();

  // https://github.com/microsoft/TypeScript/issues/24929
  dispatch<T extends ActionName>(
    action: T,
    args: ActionArgs<T>,
    options: ClusterActionDispatchOptions = {},
  ): DispatchReturnType<T> {
    return this.store.select(clusterTopologyLoaded(args.clusterId)).pipe(
      filter((loaded) => !!loaded),
      take(1),
      switchMap(() =>
        this.store.select(clusterCoordinator(args.clusterId)).pipe(
          take(1),
          map((coordinator) => {
            const id = uuid();
            this.ws.watchForReceipt(id, () => {
              this.ws.send(`/app/cluster/action/${args.clusterId}`, {
                id,
                action,
                nodeIds: isDistributed(args) ? args.nodeIds : [coordinator && coordinator.nodeId],
                argument: 'argument' in args ? (args as DispatchArgsWithArgument<any>).argument : null,
                ...(options.credentials ? { credentials: options.credentials } : {}),
              } as Omit<ClusterAction<T>, 'timestamp'>);
              this.receipts.add(id);
            });
            return id;
          }),
        ),
      ),
      switchMap((id) => {
        const sharedResponse$ = this.getResponse<T>(id, args.clusterId, id).pipe(shareReplay());
        const sharedTask$ = this.getTask(id, args.clusterId, id).pipe(shareReplay(1));
        const completedJobs$ = sharedResponse$.pipe(
          scan<ClusterActionResponse<T>, ClusterActionResponse<T>[]>(
            (acc, job) => (job.status !== ResponseStatus.RUNNING ? [...acc, job] : acc),
            [],
          ),
        );

        return combineLatest([zip(sharedResponse$, completedJobs$), sharedTask$.pipe(take(1))]).pipe(
          takeWhile(([[job, jobs], task]) => jobs.length !== task.jobCount, true),
          switchMap(([[job, jobs], task]) => {
            const result = isDistributed(args) ? { job, jobCount: task.jobCount } : job;

            if (jobs.length === task.jobCount) {
              return merge(
                of(result),
                sharedTask$.pipe(
                  takeWhile((task) => task.status === ResponseStatus.RUNNING, true),
                  switchMap((task) => {
                    if (task.status === ResponseStatus.FAILED)
                      return throwError(
                        () =>
                          jobs
                            .filter((j): j is ClusterActionFailedResponse => j.status === ResponseStatus.FAILED)
                            .map((j) => j.error) as ClusterActionResponseError[],
                      );
                    return EMPTY;
                  }),
                ),
              );
            } else return of(result);
          }),
        );
      }),
      shareReplay(),
    ) as any;
  }

  getActionsByStatus(clusterId: string, status: ResponseStatus) {
    return this.ws
      .getTopic<ClusterAction<ActionName>[]>(`/user/topic/cluster/action/${clusterId}/status/${status}`)
      .pipe(
        tap((actions) => {
          if (status !== ResponseStatus.RUNNING) actions.forEach((a) => this.receipts.delete(a.id));
        }),
      );
  }

  getTask(actionId: string, clusterId: string, receipt?: string): Observable<TaskResponse> {
    if (this.receipts.has(actionId)) return EMPTY;
    return this.ws
      .getTopic<TaskResponse>(
        `/user/topic/cluster/action/task/${clusterId}/${actionId}`,
        receipt ? { receipt } : undefined,
      )
      .pipe(takeWhile((res) => res.status === ResponseStatus.RUNNING, true));
  }

  getResponse<T extends ActionName>(
    actionId: string,
    clusterId: string,
    receipt?: string,
  ): Observable<ClusterActionResponse<T>> {
    if (this.receipts.has(actionId)) return EMPTY;
    return this.ws
      .getTopic<
        ClusterActionResponse<T>[]
      >(`/user/topic/cluster/action/job/${clusterId}/${actionId}`, receipt ? { receipt } : undefined)
      .pipe(concatMap((x) => x));
  }
}
